import { queryDirectiveBuilderChoiceInRoot } from "../../suggestion/storage/session";
import type { DirectiveSuggestionStateRef } from "../../suggestion/logic/printTable";

export const focusChoiceControlAfterSync = (el: HTMLElement): (() => void) => {
  const doc = el.ownerDocument;
  let cancelled = false;
  let rafId: number | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    cancelled = true;
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const apply = () => {
    if (cancelled) return;
    el.focus({ preventScroll: true });
  };

  apply();
  if (doc.activeElement === el) return cancel;

  let attempt = 0;
  const maxAttempts = 20;
  const tick = () => {
    if (cancelled) return;
    if (doc.activeElement === el) return;
    if (attempt >= maxAttempts) return;
    attempt += 1;
    apply();
    timeoutId = setTimeout(tick, 16);
  };

  rafId = requestAnimationFrame(() => {
    rafId = null;
    apply();
    if (cancelled || doc.activeElement === el) return;
    timeoutId = setTimeout(tick, 0);
  });

  return cancel;
};

type FloatingDirectiveBuilderSuggestionFocusArgs = {
  container: HTMLElement | null;
  explicitEl: HTMLElement | null;
};

const focusFloatingDirectiveBuilderChoiceInSuggestion = (
  args: FloatingDirectiveBuilderSuggestionFocusArgs,
): (() => void) | undefined =>
  focusFloatingDirectiveBuilderChoiceInContainer(
    args.container,
    args.explicitEl,
  );

const focusFloatingDirectiveBuilderChoiceInContainer = (
  container?: HTMLElement | null,
  explicitEl?: HTMLElement | null,
): (() => void) | undefined => {
  const el = explicitEl ?? queryDirectiveBuilderChoiceInRoot(container ?? null);
  if (el != null) {
    return focusChoiceControlAfterSync(el);
  }
  return undefined;
};

const queueFloatingDirectiveBuilderChoiceFocus = (
  container?: HTMLElement | null,
  explicitEl?: HTMLElement | null,
): (() => void) => {
  let cancelInner: (() => void) | undefined;
  const timeoutId = setTimeout(() => {
    const el =
      explicitEl ?? queryDirectiveBuilderChoiceInRoot(container ?? null);
    if (el != null) {
      cancelInner = focusChoiceControlAfterSync(el);
    }
  }, 0);
  return () => {
    clearTimeout(timeoutId);
    cancelInner?.();
  };
};

const queueFloatingDirectiveBuilderChoiceFocusForSuggestion = (
  args: FloatingDirectiveBuilderSuggestionFocusArgs,
): (() => void) =>
  queueFloatingDirectiveBuilderChoiceFocus(args.container, args.explicitEl);

export const queueFloatingDirectiveBuilderChoiceFocusAfterChipDefersToFloating =
  (): (() => void) =>
    queueFloatingDirectiveBuilderChoiceFocus(undefined, undefined);

const focusArgsFromStateRef = (
  stateRef: DirectiveSuggestionStateRef,
): FloatingDirectiveBuilderSuggestionFocusArgs => ({
  container: stateRef.floatingUiContainer ?? null,
  explicitEl: stateRef.floatingChoiceFieldEl ?? null,
});

export const blurUpdateAndFocusFloatingDirectiveChoice = (
  stateRef: DirectiveSuggestionStateRef,
): void => {
  stateRef.blurDomBeforeFloatingChoiceFocus?.();
  stateRef.update();
  focusFloatingDirectiveBuilderChoiceInSuggestion(
    focusArgsFromStateRef(stateRef),
  );
  queueFloatingDirectiveBuilderChoiceFocusForSuggestion(
    focusArgsFromStateRef(stateRef),
  );
};
